import React, { useState } from 'react';

import { AssetManager } from '../../../viewer/asset-manager';
import { ElementObj } from '../../../viewer/elements/element-obj';
import { InputHandler } from '../../../viewer/input-handler';
import { SubscriptionTopic } from '../../../viewer/messaging/pubsub';
import { SceneManager, SceneType } from '../../../viewer/scene-manager';
import { SkyView } from '../../../viewer/skyview';
import { Tool } from '../../../viewer/tools/tool';
import { useNonce } from '../../hooks/use-nonce';
import { useSubscribe } from '../../hooks/use-subscribe';

export interface SkyviewContext {
  activeTool?: Tool;
  selectedAsset?: ElementObj;
  skyview: SkyView;
  setScene: (scene: SceneType) => Promise<void>;
  activeScene?: SceneType;
}

export const SkyviewContext = React.createContext<SkyviewContext>({
  skyview: undefined as unknown as SkyView,
  setScene: () => Promise.resolve(),
});

type Props = {
  skyview: SkyView;
  children: React.ReactNode;
};

const SkyviewState = (props: Props) => {
  const [activeTool, setActiveTool] = useState(InputHandler.instance.currentTool());
  const [selectedAsset, setSelectedAsset] = useState(AssetManager.instance.getSelected());
  const [activeScene, setActiveScene] = useState<SceneType | undefined>(
    SceneManager.instance.getActiveSceneType(),
  );
  const [_, refresh] = useNonce();
  useSubscribe(SubscriptionTopic.SetTool, () => {
    setActiveTool(InputHandler.instance.currentTool());
  });

  useSubscribe(SubscriptionTopic.NewSelection, () => {
    setSelectedAsset(AssetManager.instance.getSelected());
  });
  useSubscribe(SubscriptionTopic.UnSelection, () => {
    setSelectedAsset(AssetManager.instance.getSelected());
  });
  useSubscribe(SubscriptionTopic.OnSceneChangeStarted, (message) => {
    setActiveScene(message.scene);
  });

  const setScene = React.useCallback(
    async (scene: SceneType) => {
      await SceneManager.instance.setScene(scene);
      refresh();
    },
    [refresh],
  );

  return (
    <SkyviewContext.Provider
      value={{
        skyview: props.skyview,
        activeTool: activeTool as Tool,
        selectedAsset,
        setScene,
        activeScene,
      }}
    >
      {props.children}
    </SkyviewContext.Provider>
  );
};

export { SkyviewState };
